/**
 * viewport-switcher.js — row 2's right anchor, first half.
 *
 * The device segmented control, and everything that follows from WHICH preset
 * is active: the iframe's element width, and the derived fit scale.
 *
 * ── Why this is a split and not a move ────────────────────────────────────
 * `ui/viewport-controls.js` was one class holding two products — a preset
 * switcher and a zoom control — joined only by a shared `_fitScale` number. It
 * was allowlisted in FOUR sweep rules (`no-hand-rolled-button`,
 * `no-hand-rolled-btn-class`, `no-inline-svg-literal`, `no-sized-svg-attr`),
 * and CMD-2 relocated it into row 2 without touching any of that. This is where
 * the debt is paid: every control now comes from `createButton`, every glyph
 * from `icon()`, and all four allowlist entries shrink in the same commit.
 *
 * ── The one number the two halves share ───────────────────────────────────
 * `fitScale` is DERIVED here (it is a function of the preset's intrinsic width
 * and the space the canvas actually has) and CONSUMED by `ZoomControl`, which
 * sends `userZoom × fitScale` to the iframe. The switcher does not know what a
 * zoom is; it announces that the fit moved and the zoom control decides what to
 * do about it.
 *
 * Two traps live in this half and both are covered by
 * `viewport-controls-tests.js`:
 *
 *  1. **`Number.parseFloat('100%')` is `100`.** Parsing the width
 *     unconditionally made the FLUID desktop preset report a 100px intrinsic
 *     width. Nothing calls `set()` at boot, so desktop looked correct until the
 *     first preset switch — after which the canvas was pinned to `width: 100px`
 *     and only a reload cleared it.
 *  2. **The CONTENT box, not `clientWidth`.** The wrapper carries horizontal
 *     padding, and fitting to `clientWidth` leaves the iframe exactly that much
 *     too wide — which still scrolls, just by a margin small enough to look
 *     like a rounding bug rather than the miscalculation it is.
 */

import { eventBus } from '../../core/events.js';
import { createSegmentedControl } from '../components.js';
import { icon } from '../icons.js';
import { computeFitScale } from '../../shared/touch-geometry.js';
import { NESTED_ATTR } from './focus-model.js';

/**
 * `NESTED_ATTR` is an ATTRIBUTE name (`data-cb-nested`); `dataset` is keyed by
 * its camelCase tail. Derived rather than written out, so the two cannot drift:
 * a rename of the attribute has to reach this key or the focus model silently
 * stops recognising the group and starts stealing its arrow keys.
 */
const NESTED_DATASET_KEY = NESTED_ATTR.replace(/^data-/, '').replace(/-([a-z])/g, (_, c) =>
  c.toUpperCase()
);
import { onViewportChange } from '../../shared/viewport-service.js';
import { VIEWPORTS, ZOOM_MIN, ZOOM_MAX, _setActiveViewportName } from '../viewport-controls.js';

/** Glyph per preset. The table itself no longer carries markup. */
const PRESET_GLYPH = Object.freeze({ desktop: 'desktop', tablet: 'tablet', mobile: 'mobile' });

export class ViewportSwitcher {
  /**
   * @param {HTMLElement} container
   * @param {HTMLIFrameElement} iframe
   * @param {{onFitScaleChange?: () => void}} [opts] — the seam to `ZoomControl`.
   */
  constructor(container, iframe, opts = {}) {
    this._container = container;
    this._iframe = iframe;
    this._onFitScaleChange = opts.onFitScaleChange || (() => {});
    this._current = 'desktop';
    this._fitScale = 1;
    this._buttons = new Map();
    this._labelEl = null;
    this._unsubs = [];
    this._render();
    this._listen();
  }

  destroy() {
    for (const unsub of this._unsubs) unsub?.();
    this._unsubs = [];
    this._buttons.clear();
  }

  get current() {
    return this._current;
  }

  /** Consumed by `ZoomControl`; never written anywhere. */
  get fitScale() {
    return this._fitScale;
  }

  _render() {
    // MK-1: the group, its roving tabindex and its ←/→/Home/End handling are
    // `createSegmentedControl`'s now. The class names are passed through
    // UNCHANGED, so the DOM this emits is byte-identical to CMD-5's and the
    // device-specific CSS (the 34x32 box, the hover-expanding label) still
    // applies — the migration is a change of owner, not of pixels.
    //
    // NESTED_ATTR still rides along: it is how `focus-model.js` knows to leave
    // the arrows alone here (CMD-8). Without it the row's group cursor and the
    // radiogroup's own cursor would both move on one keypress.
    this._seg = createSegmentedControl({
      ariaLabel: 'Viewport size',
      className: 'pb-device-segmented',
      optionClassName: 'pb-device-segment',
      dataset: { [NESTED_DATASET_KEY]: 'viewport' },
      value: this._current,
      options: VIEWPORTS.map((vp) => ({
        value: vp.name,
        label: vp.label,
        icon: icon(PRESET_GLYPH[vp.name], { size: 'sm' }),
        ariaLabel: vp.label,
        tooltip: vp.label,
        dataset: { viewport: vp.name }
      })),
      onChange: (name) => this.set(name)
    });
    this._group = this._seg.el;
    for (const vp of VIEWPORTS) this._buttons.set(vp.name, this._seg.optionEl(vp.name));

    this._container.appendChild(this._group);
    this._updateActive();
  }

  _listen() {
    // Rotation, a breakpoint crossing and the soft keyboard all change how much
    // room the canvas has, and none of them emits `viewport:changed`.
    this._unsubs.push(onViewportChange(() => this.recomputeFitScale()));
    this._unsubs.push(
      eventBus.on('toolbar:set-viewport', (payload) => this.set(payload?.name || payload))
    );
  }

  // ── preset ────────────────────────────────────────────────────────────────

  set(name) {
    const vp = VIEWPORTS.find((v) => v.name === name);
    if (!vp) return;
    const previous = this._current;
    this._current = name;
    _setActiveViewportName(name);

    if (this._iframe) this._iframe.style.height = vp.height;
    // A new preset is a new intrinsic width, so the fit has to be re-derived
    // BEFORE the element is sized — and before anyone reacts to viewport:changed.
    this.recomputeFitScale({ silent: true });
    this.applyPresetWidth();

    this._updateActive();
    this._updateLabel();
    this._onFitScaleChange();
    if (previous !== name) eventBus.emit('viewport:changed', { name, previous });
  }

  /**
   * MK-1: the class, `aria-checked`, the roving tabindex and `data-active` are
   * the segmented control's to write. Silent, because `set()` has already done
   * everything that follows from the change and re-entering it through
   * `onChange` would emit `viewport:changed` twice.
   *
   * `aria-pressed` is deliberately gone. It was set beside `aria-checked` on a
   * `role="radio"`, where it is invalid — a toggle button's attribute on a
   * radio. Nothing asserted it.
   */
  _updateActive() {
    this._seg?.set(this._current, { silent: true });
  }

  setLabelElement(el) {
    this._labelEl = el;
    this._updateLabel();
  }

  _updateLabel() {
    if (!this._labelEl) return;
    const vp = VIEWPORTS.find((v) => v.name === this._current);
    this._labelEl.textContent = vp
      ? `${vp.label} ${vp.width === '100%' ? '' : vp.width}`.trim()
      : '';
  }

  // ── fit ───────────────────────────────────────────────────────────────────

  /** The preset's intrinsic width in px, or null for the fluid desktop preset. */
  presetWidthPx() {
    const vp = VIEWPORTS.find((v) => v.name === this._current);
    const raw = String(vp?.width ?? '').trim();
    // Trap 1: `Number.parseFloat('100%')` is 100. The unit check is the guard.
    if (!/px$/i.test(raw)) return null;
    const px = Number.parseFloat(raw);
    return Number.isFinite(px) ? px : null;
  }

  /** Space the canvas actually has, measured from the live wrapper's CONTENT box. */
  availableWidthPx() {
    const wrap = this._iframe?.closest?.('.pb-iframe-wrap') || this._iframe?.parentElement;
    if (!wrap) return null;
    const w = wrap.clientWidth;
    if (!Number.isFinite(w) || w <= 0) return null;
    const cs = typeof getComputedStyle === 'function' ? getComputedStyle(wrap) : null;
    const pad =
      (Number.parseFloat(cs?.paddingLeft) || 0) + (Number.parseFloat(cs?.paddingRight) || 0);
    const inner = w - pad;
    return inner > 0 ? inner : w;
  }

  /**
   * Recompute the derived scale and tell the zoom control if it moved.
   *
   * Eliminating the phone's horizontal scroll rather than containing it (D9):
   * the parent overlay is sized to the UNTRANSFORMED iframe, so a CSS
   * `transform: scale()` would break every rect correlation the glass overlay
   * depends on. Routing the fit through the zoom pipeline keeps one coordinate
   * space.
   */
  recomputeFitScale({ silent = false } = {}) {
    const presetWidth = this.presetWidthPx();
    const available = this.availableWidthPx();
    // The desktop preset is fluid — already exactly as wide as the space — so
    // there is nothing to fit and the scale stays 1. That is what makes desktop
    // behaviour byte-identical by construction rather than by careful handling.
    const next =
      presetWidth === null || available === null
        ? 1
        : computeFitScale(available, presetWidth, ZOOM_MIN, ZOOM_MAX);
    if (next === this._fitScale) return;
    this._fitScale = next;
    if (silent) return;
    this.applyPresetWidth();
    this._onFitScaleChange();
  }

  /**
   * Size the iframe ELEMENT to the fitted preset.
   *
   * Both halves are required and neither works alone. `set-zoom` only sets
   * `zoom` on the iframe's documentElement, so it shrinks what is PAINTED while
   * the element stays its full preset width — leaving the canvas still scrolling
   * sideways with a band of blank iframe beside the content. Narrowing the
   * element alone would instead reflow the site to 390px, which is not what the
   * tablet preset means.
   *
   * Together they hold the layout viewport at the preset's CSS width:
   *   layout = elementWidth / zoom = (preset x fit) / (userZoom x fit)
   *          = preset / userZoom
   * which is exactly the desktop relationship, on a box that now fits.
   */
  applyPresetWidth() {
    const vp = VIEWPORTS.find((v) => v.name === this._current);
    if (!vp || !this._iframe) return;
    const presetPx = this.presetWidthPx();
    if (presetPx === null) {
      this._iframe.style.width = vp.width;
      this._iframe.style.maxWidth = '100%';
      return;
    }
    const fitted = `${Math.round(presetPx * this._fitScale)}px`;
    this._iframe.style.width = fitted;
    this._iframe.style.maxWidth = fitted;
  }
}
